/**
 * Leasing Season Config — property-owned bundle of six artifact slots.
 * Season binds one bundle; artifacts remain separately stored (snapshot / copy-on-adopt).
 */

import type { WorkbookSectionsState } from '../PipelineFlowDemo/workbookSectionDraft'
import type { ConditionalApprovalsPolicy } from './conditionalApprovals'
import {
    defaultConditionalApprovalsPolicy,
    normalizeQualifyingMethodsPolicy,
} from './conditionalApprovals'
import {
    GUEST_CARD_SLOTS,
    MemberConfiguredMap,
    SelectedSetupConfig,
    SetupArtifactKind,
    SetupConfigOption,
    emptyMemberMap,
    nowIso,
} from './setupArtifacts'
import type { LeasePacketMember } from './signableDocRoles'
import {
    emptyGuarantorPacket,
    emptyLeasePacket,
    normalizeSignableDocSetDefinition,
} from './signableDocRoles'
import type {
    ApiCreatedFrom,
    ApiLeasingSeasonConfigDefinition,
    ApiSlotRef,
    PropertyLeasingSeasonConfigDetail,
    PropertyLeasingSeasonConfigListItem,
} from './useLeasingSeasonConfigApi'

export type SeasonConfigSlotId =
    | 'applicant_data'
    | 'guest_card'
    | 'application_fee'
    | 'conditional_approvals'
    | 'signable_docs'
    | 'property_data'
    | 'leads_pipeline'

/** Setup slots (Compose pipeline is separate). */
export type SeasonConfigSetupSlotId = Exclude<
    SeasonConfigSlotId,
    'leads_pipeline'
>

export type EditScope = 'this_config_only' | 'shared_artifact'

export type PipelineSlotRef = {
    configId: number | null
    name: string | null
    orgMandated?: boolean
    /** How many other season configs pin this pipeline. */
    usedByOtherConfigs?: number
}

export type SeasonConfigBundleSlots = {
    applicant_data: SelectedSetupConfig | null
    guest_card: SelectedSetupConfig | null
    application_fee: SelectedSetupConfig | null
    conditional_approvals: SelectedSetupConfig | null
    signable_docs: SelectedSetupConfig | null
    property_data: SelectedSetupConfig | null
    leads_pipeline: PipelineSlotRef
}

export type SeasonConfigBundle = {
    id: string
    name: string
    modifiedAt: string
    /** How this bundle was created on the property. */
    createdFrom: 'existing' | 'ezos' | 'org' | 'scratch'
    orgMandated?: boolean
    /** False when bound to a season that has started (or org-mandated). */
    isEditable?: boolean
    /** Active season currently using this config, if any. */
    activeBoundSeason?: { id: number; name: string } | null
    /** Season using this config, if any (1:1). */
    boundSeason?: {
        id: number
        name: string
        status?: 'active' | 'upcoming' | 'past'
    } | null
    description?: string
    slots: SeasonConfigBundleSlots
    /**
     * Per-setup-slot edit scope while working inside this bundle.
     * Retained on the API; the editor now forks via Create a copy instead of
     * switching shared vs this-config-only.
     */
    editScopeBySlot: Partial<Record<SeasonConfigSetupSlotId, EditScope>>
}

export type SeasonConfigBundleOption = {
    id: string
    name: string
    description: string
    modifiedAt: string
    orgMandated?: boolean
    createdFrom?: SeasonConfigBundle['createdFrom']
    isEditable?: boolean
    activeBoundSeason?: { id: number; name: string } | null
    boundSeason?: {
        id: number
        name: string
        status?: 'active' | 'upcoming' | 'past'
    } | null
    readinessLabel?: string
}

export const SEASON_CONFIG_SLOT_META: Record<
    SeasonConfigSlotId,
    {
        label: string
        shortLabel: string
        required: boolean
        description: string
    }
> = {
    applicant_data: {
        label: 'Applicant Data',
        shortLabel: 'Applicant Data',
        required: true,
        description: 'Workbook sections and questions',
    },
    guest_card: {
        label: 'Guest Card',
        shortLabel: 'Guest Card',
        required: true,
        description: 'Lead intake channel pack',
    },
    application_fee: {
        label: 'Application Fee',
        shortLabel: 'App Fee',
        required: true,
        description: 'Applicant fee policy',
    },
    conditional_approvals: {
        label: 'Qualifying Methods',
        shortLabel: 'Qualify',
        required: false,
        description: 'Self, guarantor, Leap, deposit, prepay',
    },
    signable_docs: {
        label: 'Signable Documents',
        shortLabel: 'Signable Docs',
        required: true,
        description: 'Document role assignments',
    },
    property_data: {
        label: 'Property Data',
        shortLabel: 'Property Data',
        required: true,
        description: 'Legal identity, lease terms, rent by floor plan',
    },
    leads_pipeline: {
        label: 'Leads Pipeline',
        shortLabel: 'Pipeline',
        required: true,
        description: 'Compose timeline phases and gates',
    },
}

export const SEASON_CONFIG_SETUP_SLOTS: SeasonConfigSetupSlotId[] = [
    'conditional_approvals',
    'applicant_data',
    'guest_card',
    'application_fee',
    'property_data',
    'signable_docs',
]

export function emptyPipelineSlot(): PipelineSlotRef {
    return { configId: null, name: null }
}

export function emptyBundleSlots(): SeasonConfigBundleSlots {
    return {
        applicant_data: null,
        guest_card: null,
        application_fee: null,
        conditional_approvals: null,
        signable_docs: null,
        property_data: null,
        leads_pipeline: emptyPipelineSlot(),
    }
}

export function slotIsFilled(
    slots: SeasonConfigBundleSlots,
    slotId: SeasonConfigSlotId,
): boolean {
    if (slotId === 'leads_pipeline') {
        return slots.leads_pipeline.configId != null
    }
    return slots[slotId] != null
}

export function bundleReadiness(
    bundle: SeasonConfigBundle,
): {
    ready: boolean
    requiredReady: number
    requiredTotal: number
    label: string
} {
    const required = (Object.keys(
        SEASON_CONFIG_SLOT_META,
    ) as SeasonConfigSlotId[]).filter(
        (id) => SEASON_CONFIG_SLOT_META[id].required,
    )
    const requiredReady = required.filter((id) =>
        slotIsFilled(bundle.slots, id),
    ).length
    const ready = requiredReady === required.length
    return {
        ready,
        requiredReady,
        requiredTotal: required.length,
        label: ready
            ? 'Ready to apply to a season'
            : requiredReady === 0
            ? 'Not configured yet'
            : `${requiredReady}/${required.length} required slots`,
    }
}

export function artifactUsedByOthers(
    selected: SelectedSetupConfig | null | undefined,
): number {
    return selected?.usedByOtherConfigs ?? 0
}

export function forkSetupSlot(
    selected: SelectedSetupConfig,
    bundleName: string,
): SelectedSetupConfig {
    return {
        ...selected,
        id: `fork-${selected.id}-${Date.now()}`,
        name: `${selected.name} (${bundleName})`,
        orgMandated: false,
        usedByOtherConfigs: 0,
        modifiedAt: nowIso(),
    }
}

export function setupSlotKind(
    slotId: SeasonConfigSetupSlotId,
): SetupArtifactKind {
    return slotId
}

/** Ensure catalog has the selected option for Edit existing lanes. */
export function ensureCatalogHas(
    existing: SetupConfigOption[],
    selected: SelectedSetupConfig | null,
): SetupConfigOption[] {
    if (!selected) return existing
    if (existing.some((o) => o.id === selected.id)) return existing
    return [
        {
            id: selected.id,
            name: selected.name,
            description: 'Pinned by leasing season config',
            modifiedAt: selected.modifiedAt ?? nowIso(),
            orgMandated: selected.orgMandated,
        },
        ...existing,
    ]
}

function createdFromToApi(
    createdFrom: SeasonConfigBundle['createdFrom'],
): ApiCreatedFrom {
    if (createdFrom === 'existing') return 'local'
    return createdFrom
}

function createdFromFromApi(
    createdFrom: ApiCreatedFrom,
): SeasonConfigBundle['createdFrom'] {
    if (createdFrom === 'local') return 'existing'
    return createdFrom
}

function persistedSetupPackId(
    id: string | number | null | undefined,
): number | null {
    if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0 ? id : null
    }
    if (typeof id === 'string' && /^\d+$/.test(id)) {
        return Number(id)
    }
    return null
}

function setupSlotToApi(selected: SelectedSetupConfig | null): ApiSlotRef {
    if (!selected) return null
    const packId = persistedSetupPackId(selected.id)
    if (packId == null) return null
    return {
        id: packId,
        name: selected.name,
        source: selected.source,
        org_mandated: !!selected.orgMandated,
        used_by_other_configs: selected.usedByOtherConfigs ?? 0,
        adopted_from_name: selected.adoptedFromName ?? null,
        adopted_from_source: selected.adoptedFromSource ?? null,
        modified_at: selected.modifiedAt ?? null,
    }
}

function setupSlotFromApi(raw: ApiSlotRef): SelectedSetupConfig | null {
    if (!raw || raw.name == null) return null
    const packId = persistedSetupPackId(raw.id)
    if (packId == null) return null
    const id = String(packId)
    const source =
        raw.source === 'ezos' ||
        raw.source === 'org' ||
        raw.source === 'scratch' ||
        raw.source === 'existing'
            ? raw.source
            : 'existing'
    return {
        id,
        name: String(raw.name),
        source,
        orgMandated: !!raw.org_mandated,
        usedByOtherConfigs: Number(raw.used_by_other_configs ?? 0),
        adoptedFromName: raw.adopted_from_name ?? undefined,
        adoptedFromSource:
            raw.adopted_from_source === 'ezos' ||
            raw.adopted_from_source === 'org' ||
            raw.adopted_from_source === 'scratch'
                ? raw.adopted_from_source
                : undefined,
        modifiedAt: raw.modified_at ?? undefined,
    }
}

function pipelineSlotToApi(slot: PipelineSlotRef): ApiSlotRef {
    if (slot.configId == null) return null
    return {
        config_id: slot.configId,
        id: slot.configId,
        name: slot.name,
        org_mandated: !!slot.orgMandated,
        used_by_other_configs: slot.usedByOtherConfigs ?? 0,
    }
}

function pipelineSlotFromApi(raw: ApiSlotRef): PipelineSlotRef {
    if (!raw) return emptyPipelineSlot()
    const configId =
        typeof raw.config_id === 'number'
            ? raw.config_id
            : typeof raw.config_id === 'string' && /^\d+$/.test(raw.config_id)
            ? Number(raw.config_id)
            : typeof raw.id === 'number'
            ? raw.id
            : typeof raw.id === 'string' && /^\d+$/.test(raw.id)
            ? Number(raw.id)
            : null
    return {
        configId,
        name: raw.name != null ? String(raw.name) : null,
        orgMandated: !!raw.org_mandated,
        usedByOtherConfigs: Number(raw.used_by_other_configs ?? 0),
    }
}

export type BundleDraftExtras = {
    guestCardMembers: MemberConfiguredMap
    guestCardChannels?: GuestCardChannelBindings
    conditionalPolicy: ConditionalApprovalsPolicy
    leasePacket: LeasePacketMember[]
    guarantorPacket: LeasePacketMember[]
    workbookSections: WorkbookSectionsState
    feeAmount?: string
    feeCurrency?: string
}

/** Channel → GuestCard id (stringified) for Setup UI. */
export type GuestCardChannelBindings = Record<string, string | null>

export function emptyGuestCardChannelBindings(): GuestCardChannelBindings {
    const next: GuestCardChannelBindings = {}
    for (const slot of GUEST_CARD_SLOTS) {
        next[slot.id] = null
    }
    return next
}

/** Pack definition written to PropertyGuestCardPack. */
export function guestCardPackDefinitionFromChannels(
    channelsIn: GuestCardChannelBindings,
): Record<string, unknown> {
    const channels: Record<string, number | null> = {}
    const configured_channels: Record<string, boolean> = {}
    for (const slot of GUEST_CARD_SLOTS) {
        const raw = channelsIn[slot.id]
        const parsed =
            raw != null && raw !== '' && /^\d+$/.test(raw) ? Number(raw) : null
        channels[slot.id] = parsed
        configured_channels[slot.id] = parsed != null
    }
    return {
        channels,
        extra_guest_card_ids: [],
        configured_channels,
    }
}

/** @deprecated Prefer guestCardPackDefinitionFromChannels */
export function guestCardPackDefinitionFromMembers(
    members: MemberConfiguredMap,
): Record<string, unknown> {
    const channels: GuestCardChannelBindings = emptyGuestCardChannelBindings()
    for (const slot of GUEST_CARD_SLOTS) {
        channels[slot.id] = members[slot.id] ? 'configured' : null
    }
    // Booleans alone cannot assign GuestCard ids — keep configured flags only.
    return {
        channels: Object.fromEntries(
            GUEST_CARD_SLOTS.map((slot) => [slot.id, null]),
        ),
        extra_guest_card_ids: [],
        configured_channels: Object.fromEntries(
            GUEST_CARD_SLOTS.map((slot) => [
                slot.id,
                Boolean(members[slot.id]),
            ]),
        ),
    }
}

export function guestCardChannelBindingsFromPackDefinition(
    definition: Record<string, unknown> | null | undefined,
): GuestCardChannelBindings {
    const bindings = emptyGuestCardChannelBindings()
    if (!definition) return bindings
    const channelIds = definition.channels as
        | Record<string, unknown>
        | undefined
    if (channelIds) {
        for (const slot of GUEST_CARD_SLOTS) {
            const value = channelIds[slot.id]
            if (value == null || value === '') {
                bindings[slot.id] = null
            } else {
                bindings[slot.id] = String(value)
            }
        }
    }
    return bindings
}

export function guestCardMembersFromPackDefinition(
    definition: Record<string, unknown> | null | undefined,
): MemberConfiguredMap {
    const members = emptyMemberMap(GUEST_CARD_SLOTS)
    const bindings = guestCardChannelBindingsFromPackDefinition(definition)
    const configuredChannels = definition?.configured_channels as
        | Record<string, boolean>
        | undefined
    for (const slot of GUEST_CARD_SLOTS) {
        members[slot.id] =
            bindings[slot.id] != null || Boolean(configuredChannels?.[slot.id])
    }
    return members
}

/** Pack definition written to PropertySignableDocSet. */
export function signableDocSetDefinitionFromPackets(
    leasePacket: LeasePacketMember[] = [],
    guarantorPacket: LeasePacketMember[] = [],
): Record<string, unknown> {
    return {
        lease_packet: leasePacket.map((member) => ({
            id: member.id,
            docId: member.docId,
            rule: { ...member.rule },
        })),
        guarantor_packet: guarantorPacket.map((member) => ({
            id: member.id,
            docId: member.docId,
            rule: { ...member.rule },
        })),
    }
}

/** @deprecated Use signableDocSetDefinitionFromPackets */
export function signableDocSetDefinitionFromBindings(
    _bindings: unknown,
    leasePacket: LeasePacketMember[] = [],
    guarantorPacket: LeasePacketMember[] = [],
): Record<string, unknown> {
    return signableDocSetDefinitionFromPackets(leasePacket, guarantorPacket)
}

export function leasePacketFromPackDefinition(
    definition: Record<string, unknown> | null | undefined,
): LeasePacketMember[] {
    return normalizeSignableDocSetDefinition(definition).lease_packet
}

export function guarantorPacketFromPackDefinition(
    definition: Record<string, unknown> | null | undefined,
): LeasePacketMember[] {
    return normalizeSignableDocSetDefinition(definition).guarantor_packet
}

export function conditionalPolicyFromPackDefinition(
    definition: Record<string, unknown> | null | undefined,
): ConditionalApprovalsPolicy {
    return normalizeQualifyingMethodsPolicy(definition)
}

export function setupPackToCatalogOption(pack: {
    id: number
    name: string
    description?: string
    modified_date?: string
}): SetupConfigOption {
    return {
        id: String(pack.id),
        name: pack.name,
        description: pack.description || '',
        modifiedAt: pack.modified_date,
    }
}

/** Merge API pack rows into the Setup chooser catalog (API wins on id clash). */
export function mergePackCatalog(
    existing: SetupConfigOption[],
    packs: Array<{
        id: number
        name: string
        description?: string
        modified_date?: string
    }>,
): SetupConfigOption[] {
    const byId = new Map(existing.map((row) => [row.id, row]))
    for (const pack of packs) {
        byId.set(String(pack.id), setupPackToCatalogOption(pack))
    }
    return Array.from(byId.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
    )
}

export function buildApiDefinition(
    slots: SeasonConfigBundleSlots,
    editScopeBySlot: SeasonConfigBundle['editScopeBySlot'],
    extras: BundleDraftExtras,
): ApiLeasingSeasonConfigDefinition {
    return {
        slots: {
            applicant_data: setupSlotToApi(slots.applicant_data),
            guest_card: setupSlotToApi(slots.guest_card),
            application_fee: setupSlotToApi(slots.application_fee),
            conditional_approvals: setupSlotToApi(slots.conditional_approvals),
            signable_docs: setupSlotToApi(slots.signable_docs),
            property_data: setupSlotToApi(slots.property_data),
            leads_pipeline: pipelineSlotToApi(slots.leads_pipeline),
        },
        edit_scope_by_slot: { ...editScopeBySlot },
        guest_card_members: { ...extras.guestCardMembers },
        conditional_policy: (extras.conditionalPolicy as unknown) as Record<
            string,
            unknown
        >,
        lease_packet: extras.leasePacket.map((member) => ({
            id: member.id,
            docId: member.docId,
            rule: { ...member.rule },
        })),
        guarantor_packet: extras.guarantorPacket.map((member) => ({
            id: member.id,
            docId: member.docId,
            rule: { ...member.rule },
        })),
        workbook_sections: extras.workbookSections as Record<string, unknown>,
    }
}

export function starterApiDefinition(
    kind: 'ezos' | 'org' | 'scratch',
): ApiLeasingSeasonConfigDefinition {
    // Empty slots — scratch stays empty; EZOS / org fork starter rows server-side.
    void kind
    return buildApiDefinition(
        emptyBundleSlots(),
        {},
        {
            guestCardMembers: emptyMemberMap(GUEST_CARD_SLOTS),
            conditionalPolicy: defaultConditionalApprovalsPolicy(),
            leasePacket: emptyLeasePacket(),
            guarantorPacket: emptyGuarantorPacket(),
            workbookSections: {},
        },
    )
}

/** Required slot FKs on list/detail — matches SEASON_CONFIG_SLOT_META.required. */
export function listItemRequiredSlotIds(
    item: Pick<
        PropertyLeasingSeasonConfigListItem,
        | 'application_workbook_id'
        | 'guest_card_pack_id'
        | 'fee_policy_id'
        | 'signable_doc_set_id'
        | 'pipeline_config_id'
        | 'property_data_pack_id'
    >,
): Array<number | null | undefined> {
    return [
        item.application_workbook_id,
        item.guest_card_pack_id,
        item.fee_policy_id,
        item.signable_doc_set_id,
        item.property_data_pack_id,
        item.pipeline_config_id,
    ]
}

export function listItemReadinessLabel(
    item: Pick<
        PropertyLeasingSeasonConfigListItem,
        | 'application_workbook_id'
        | 'guest_card_pack_id'
        | 'fee_policy_id'
        | 'signable_doc_set_id'
        | 'pipeline_config_id'
        | 'property_data_pack_id'
        | 'is_ready'
        | 'readiness_label'
    >,
): string {
    if (item.readiness_label) {
        return item.readiness_label
    }
    if (item.is_ready) {
        return 'Ready to apply to a season'
    }
    const required = listItemRequiredSlotIds(item)
    const requiredReady = required.filter((id) => id != null).length
    if (requiredReady === required.length) {
        return 'Ready to apply to a season'
    }
    if (requiredReady === 0) {
        return 'Not configured yet'
    }
    return `${requiredReady}/${required.length} required slots`
}

export function listItemToBundleOption(
    item: PropertyLeasingSeasonConfigListItem,
): SeasonConfigBundleOption {
    return {
        id: String(item.id),
        name: item.name,
        description: item.description || 'Property leasing season config',
        modifiedAt: item.modified_date ?? nowIso(),
        createdFrom: createdFromFromApi(item.created_from),
        orgMandated: item.org_mandated,
        isEditable: item.is_editable,
        activeBoundSeason: item.active_bound_season ?? null,
        boundSeason: item.bound_season ?? null,
        readinessLabel: listItemReadinessLabel(item),
    }
}

/** Status line for chooser / bundle bar. */
export function bundleStatusLabel(option: {
    isEditable?: boolean
    activeBoundSeason?: { id: number; name: string } | null
    boundSeason?: { id: number; name: string; status?: string } | null
    ready?: boolean
    readinessLabel?: string
}): string {
    const boundName = option.boundSeason?.name || option.activeBoundSeason?.name
    if (boundName) {
        const locked = option.isEditable === false ? ' · Locked' : ''
        return `Used by ${boundName}${locked}`
    }
    const ready = option.readinessLabel
    return ready ? `Draft · ${ready}` : 'Draft'
}

function boundSeasonRank(option: SeasonConfigBundleOption): number {
    const status = option.boundSeason?.status
    if (status === 'active') return 0
    if (status === 'upcoming') return 1
    if (!status) return 2
    return 3
}

/** This season, then drafts, then past. */
export function sortExistingBundleOptions(
    options: SeasonConfigBundleOption[],
): SeasonConfigBundleOption[] {
    return [...options].sort((a, b) => {
        const rank = boundSeasonRank(a) - boundSeasonRank(b)
        if (rank !== 0) return rank
        return (b.modifiedAt || '').localeCompare(a.modifiedAt || '')
    })
}

export function bundleFromApiDetail(
    detail: PropertyLeasingSeasonConfigDetail,
): {
    bundle: SeasonConfigBundle
    extras: BundleDraftExtras
} {
    const def = detail.definition || starterApiDefinition('scratch')
    const slots: SeasonConfigBundleSlots = {
        applicant_data: setupSlotFromApi(def.slots?.applicant_data ?? null),
        guest_card: setupSlotFromApi(def.slots?.guest_card ?? null),
        application_fee: setupSlotFromApi(def.slots?.application_fee ?? null),
        conditional_approvals: setupSlotFromApi(
            def.slots?.conditional_approvals ?? null,
        ),
        signable_docs: setupSlotFromApi(def.slots?.signable_docs ?? null),
        property_data: setupSlotFromApi(def.slots?.property_data ?? null),
        leads_pipeline: pipelineSlotFromApi(def.slots?.leads_pipeline ?? null),
    }
    if (
        detail.pipeline_config_id != null &&
        slots.leads_pipeline.configId == null
    ) {
        slots.leads_pipeline = {
            configId: detail.pipeline_config_id,
            name:
                detail.pipeline_config_name ??
                `Pipeline #${detail.pipeline_config_id}`,
            usedByOtherConfigs: slots.leads_pipeline.usedByOtherConfigs,
            orgMandated: slots.leads_pipeline.orgMandated,
        }
    }
    // Prefer denormalized pack FKs when slot summaries are missing.
    if (!slots.guest_card && detail.guest_card_pack) {
        slots.guest_card = setupSlotFromApi({
            id: detail.guest_card_pack.id,
            name: detail.guest_card_pack.name,
            source: 'existing',
        })
    }
    if (!slots.signable_docs && detail.signable_doc_set) {
        slots.signable_docs = setupSlotFromApi({
            id: detail.signable_doc_set.id,
            name: detail.signable_doc_set.name,
            source: 'existing',
        })
    }
    if (!slots.application_fee && detail.fee_policy) {
        slots.application_fee = setupSlotFromApi({
            id: detail.fee_policy.id,
            name: detail.fee_policy.name,
            source: 'existing',
        })
    }
    if (!slots.conditional_approvals && detail.conditional_approvals) {
        slots.conditional_approvals = setupSlotFromApi({
            id: detail.conditional_approvals.id,
            name: detail.conditional_approvals.name,
            source: 'existing',
        })
    }
    if (!slots.property_data && detail.property_data_pack) {
        slots.property_data = setupSlotFromApi({
            id: detail.property_data_pack.id,
            name: detail.property_data_pack.name,
            source: 'existing',
        })
    }

    const bundle: SeasonConfigBundle = {
        id: String(detail.id),
        name: detail.name,
        modifiedAt: detail.modified_date ?? nowIso(),
        createdFrom: createdFromFromApi(detail.created_from),
        orgMandated: detail.org_mandated,
        isEditable: detail.is_editable,
        activeBoundSeason: detail.active_bound_season ?? null,
        boundSeason: detail.bound_season ?? null,
        description: detail.description,
        slots,
        editScopeBySlot: {
            ...(def.edit_scope_by_slot as SeasonConfigBundle['editScopeBySlot']),
        },
    }

    const guestChannels = detail.guest_card_pack
        ? guestCardChannelBindingsFromPackDefinition(
              detail.guest_card_pack.definition,
          )
        : emptyGuestCardChannelBindings()
    const guestMembers = detail.guest_card_pack
        ? guestCardMembersFromPackDefinition(detail.guest_card_pack.definition)
        : {
              ...emptyMemberMap(GUEST_CARD_SLOTS),
              ...(def.guest_card_members || {}),
          }

    const extras: BundleDraftExtras = {
        guestCardMembers: guestMembers,
        guestCardChannels: guestChannels,
        conditionalPolicy: detail.conditional_approvals
            ? conditionalPolicyFromPackDefinition(
                  detail.conditional_approvals.definition,
              )
            : normalizeQualifyingMethodsPolicy(def.conditional_policy),
        leasePacket: detail.signable_doc_set
            ? leasePacketFromPackDefinition(detail.signable_doc_set.definition)
            : normalizeSignableDocSetDefinition({
                  role_bindings: def.signable_role_bindings || {},
                  lease_packet: def.lease_packet || [],
                  guarantor_packet: def.guarantor_packet || [],
              }).lease_packet,
        guarantorPacket: detail.signable_doc_set
            ? guarantorPacketFromPackDefinition(
                  detail.signable_doc_set.definition,
              )
            : normalizeSignableDocSetDefinition({
                  role_bindings: def.signable_role_bindings || {},
                  lease_packet: def.lease_packet || [],
                  guarantor_packet: def.guarantor_packet || [],
              }).guarantor_packet,
        workbookSections: (def.workbook_sections ||
            {}) as WorkbookSectionsState,
        feeAmount: detail.fee_policy?.amount,
        feeCurrency: detail.fee_policy?.currency,
    }

    return { bundle, extras }
}

export { createdFromToApi }
